import React, { useEffect, useState } from "react";

import {
    FaPoundSign,
    FaCheckCircle,
    FaTimesCircle,
    FaShieldAlt,
    FaStar,
    FaAward,
    FaRocket,
    FaGem,
    FaCrown,
    FaCalendarAlt
} from "react-icons/fa";

import API_BASE_URL from "../config";
import { useLocation, useNavigate } from "react-router-dom";

import Header from "../components/Header";
import Footer from "../components/Footer";

import {
    PayPalScriptProvider,
    PayPalButtons
} from "@paypal/react-paypal-js";

import "../styles/Subscription.css";
import { toast } from "react-toastify";


export default function Subscription() {

    const location = useLocation();
    const navigate = useNavigate();

    const hideLayout =
        location.pathname === "/solicitor/dashboard";

    /* =====================================================
       EXISTING LOGGED-IN TOKEN
       ===================================================== */

    const token = sessionStorage.getItem("token");

    const paypalClientId =
        process.env.REACT_APP_PAYPAL_CLIENT_ID;


    /* =====================================================
       SIGNUP DATA
       IMPORTANT:
       Signup page sends this through navigate()
       ===================================================== */

    const signupData =
        location.state?.signupData || null;


    /* =====================================================
       STATE
       ===================================================== */

    const [subscription, setSubscription] =
        useState(null);

    const [selectedPlan, setSelectedPlan] =
        useState(null);

    const [showPayment, setShowPayment] =
        useState(false);

    const [processingPlan, setProcessingPlan] =
        useState(null);


    /* =====================================================
       DEBUG
       ===================================================== */

    useEffect(() => {

        console.log(
            "========== SUBSCRIPTION PAGE =========="
        );

        console.log(
            "Existing Token:",
            token ? "YES" : "NO"
        );

        console.log(
            "Signup Data:",
            signupData
        );

        console.log(
            "========================================"
        );

    }, [token, signupData]);


    /* =====================================================
       FETCH CURRENT SUBSCRIPTION
       ONLY FOR ALREADY LOGGED-IN USERS
       ===================================================== */

    useEffect(() => {

        const fetchSubscription = async () => {

            if (!token) {
                return;
            }

            try {

                const res = await fetch(
                    `${API_BASE_URL}/api/auth/subscription`,
                    {
                        headers: {
                            Authorization:
                                `Bearer ${token}`
                        }
                    }
                );

                if (!res.ok) {
                    return;
                }

                const data =
                    await res.json();

                if (data.subscription) {

                    setSubscription(
                        data.subscription
                    );

                }

            } catch (err) {

                console.error(
                    "Subscription fetch error:",
                    err
                );

            }

        };

        fetchSubscription();

    }, [token]);


    /* =====================================================
       PLAN STATE
       ===================================================== */

    const isActive =
        subscription?.subscriptionStatus === "ACTIVE";

    const isExpired =
        subscription?.subscriptionStatus === "EXPIRED";

    const currentPlan =
        subscription?.planName;

    const isPaidActive =
        isActive &&
        (
            currentPlan === "PROFESSIONAL" ||
            currentPlan === "ENTERPRISE"
        );


    /* =====================================================
       HANDLE PLAN CLICK
       ===================================================== */

    const handlePlanClick = (plan) => {

        /*
         * Existing logged-in user
         */
        if (token) {

            if (
                currentPlan !== "FREE" &&
                plan === "FREE" &&
                isActive
            ) {
                return;
            }

        }


        /*
         * FREE PLAN
         */
        if (plan === "FREE") {

            /*
             * New signup user cannot use
             * protected free-trial API yet.
             *
             * For new signup, we will create the
             * account only after payment.
             *
             * So if they want FREE trial,
             * we create account directly using
             * completeSignup.
             */

            if (!token && signupData) {

                handleFreeTrialForNewSignup();

                return;
            }

            if (token) {

                handleFreeTrial();

                return;
            }

            toast.error(
                "Please complete signup first."
            );

            return;
        }


        /*
         * PAID PLAN
         */

        setSelectedPlan(plan);
        setShowPayment(true);

    };


    /* =====================================================
       FREE TRIAL FOR EXISTING LOGGED-IN USER
       ===================================================== */

    const handleFreeTrial = async () => {

        setProcessingPlan("FREE");

        try {

            const response = await fetch(
                `${API_BASE_URL}/api/auth/subscription/free-trial`,
                {
                    method: "POST",

                    headers: {
                        "Content-Type":
                            "application/json",

                        Authorization:
                            `Bearer ${token}`
                    }
                }
            );

            const data =
                await response.json();

            if (response.ok) {

                toast.success(
                    "Free trial activated successfully!"
                );

                setTimeout(() => {
                    window.location.reload();
                }, 1500);

            } else {

                toast.error(
                    data.message ||
                    "Failed to activate free trial"
                );

            }

        } catch (error) {

            console.error(
                "Free trial error:",
                error
            );

            toast.error(
                "Something went wrong"
            );

        } finally {

            setProcessingPlan(null);

        }

    };


    /* =====================================================
       FREE TRIAL FOR NEW SIGNUP USER
       ===================================================== */

    const handleFreeTrialForNewSignup =
        async () => {

            if (!signupData) {

                toast.error(
                    "Signup information is missing."
                );

                return;
            }

            setProcessingPlan("FREE");

            try {

                console.log(
                    "Creating FREE account:",
                    signupData
                );


                const response = await fetch(
                    `${API_BASE_URL}/api/auth/complete-signup`,
                    {
                        method: "POST",

                        headers: {
                            "Content-Type":
                                "application/json"
                        },

                        body: JSON.stringify({

                            signupData,

                            paymentId:
                                "FREE_TRIAL",

                            planName:
                                "FREE",

                            planType:
                                "TRIAL",

                            amount: 0,

                            paymentStatus:
                                "SUCCESS"

                        })
                    }
                );


                const data =
                    await response.json();


                console.log(
                    "Complete Signup Response:",
                    data
                );


                if (!response.ok) {

                    toast.error(
                        data.message ||
                        "Failed to create account"
                    );

                    return;
                }


                /*
                 * Save login information
                 */

                sessionStorage.setItem(
                    "token",
                    data.token
                );

                sessionStorage.setItem(
                    "user",
                    JSON.stringify(data.user)
                );


                toast.success(
                    "Account created successfully!"
                );


                /*
                 * Clear signup state
                 */

                navigate(
                    "/solicitor/dashboard",
                    {
                        replace: true
                    }
                );


            } catch (error) {

                console.error(
                    "Complete signup error:",
                    error
                );

                toast.error(
                    "Failed to create account"
                );

            } finally {

                setProcessingPlan(null);

            }

        };


    /* =====================================================
       🔥 PAYMENT SUCCESS
       IMPORTANT FIX
       ===================================================== */

    const handlePaymentSuccess = async (
        details,
        planName,
        planType,
        amount
    ) => {

        try {

            console.log(
                "========== PAYMENT SUCCESS =========="
            );

            console.log(
                "Payment ID:",
                details?.id
            );

            console.log(
                "Plan:",
                planName
            );

            console.log(
                "Plan Type:",
                planType
            );

            console.log(
                "Amount:",
                amount
            );

            console.log(
                "Signup Data:",
                signupData
            );

            console.log(
                "Existing Token:",
                token ? "YES" : "NO"
            );

            console.log(
                "====================================="
            );


            /*
             * =================================================
             * NEW SIGNUP USER
             * =================================================
             *
             * New solicitor does NOT have token yet.
             *
             * So DO NOT call:
             *
             * /api/auth/subscription/activate
             *
             * because that endpoint requires req.user.id.
             *
             * Instead call:
             *
             * /api/auth/complete-signup
             */

            if (!token) {

                if (!signupData) {

                    toast.error(
                        "Signup data is missing. Please restart signup."
                    );

                    return;
                }


                const response = await fetch(
                    `${API_BASE_URL}/api/auth/complete-signup`,
                    {
                        method: "POST",

                        headers: {
                            "Content-Type":
                                "application/json"
                        },

                        body: JSON.stringify({

                            signupData,

                            paymentId:
                                details.id,

                            planName,

                            planType,

                            amount,

                            paymentStatus:
                                "SUCCESS"

                        })
                    }
                );


                const data =
                    await response.json();


                console.log(
                    "Complete Signup Status:",
                    response.status
                );

                console.log(
                    "Complete Signup Response:",
                    data
                );


                if (!response.ok) {

                    toast.error(
                        data.message ||
                        "Account creation failed after payment"
                    );

                    return;
                }


                /*
                 * ============================================
                 * SAVE TOKEN
                 * ============================================
                 */

                sessionStorage.setItem(
                    "token",
                    data.token
                );


                sessionStorage.setItem(
                    "user",
                    JSON.stringify(data.user)
                );


                /*
                 * Remove payment modal
                 */

                setShowPayment(false);
                setSelectedPlan(null);


                toast.success(
                    "Payment successful! Account created successfully."
                );


                /*
                 * ============================================
                 * REDIRECT
                 * ============================================
                 */

                setTimeout(() => {

                    if (
                        data.user?.role ===
                        "SOLICITOR"
                    ) {

                        navigate(
                            "/solicitor/dashboard",
                            {
                                replace: true
                            }
                        );

                    } else {

                        navigate(
                            "/user/dashboard",
                            {
                                replace: true
                            }
                        );

                    }

                }, 1000);


                return;
            }


            /*
             * =================================================
             * EXISTING LOGGED-IN USER
             * =================================================
             *
             * Existing users should continue using the
             * normal subscription activation endpoint.
             */

            const response = await fetch(
                `${API_BASE_URL}/api/auth/subscription/activate`,
                {
                    method: "POST",

                    headers: {
                        "Content-Type":
                            "application/json",

                        Authorization:
                            `Bearer ${token}`
                    },

                    body: JSON.stringify({

                        paymentId:
                            details.id,

                        planName,

                        planType,

                        amount,

                        paymentStatus:
                            "SUCCESS"

                    })
                }
            );


            const data =
                await response.json();


            if (!response.ok) {

                toast.error(
                    data.message ||
                    "Failed to activate subscription"
                );

                return;
            }


            toast.success(
                `Payment successful! ${planName} plan activated.`
            );


            setShowPayment(false);
            setSelectedPlan(null);


            /*
             * Refresh subscription
             */

            const subRes =
                await fetch(
                    `${API_BASE_URL}/api/auth/subscription`,
                    {
                        headers: {
                            Authorization:
                                `Bearer ${token}`
                        }
                    }
                );


            const subData =
                await subRes.json();


            if (subData.subscription) {

                setSubscription(
                    subData.subscription
                );

            }


            setTimeout(() => {

                window.location.reload();

            }, 1500);


        } catch (error) {

            console.error(
                "Payment activation error:",
                error
            );

            toast.error(
                "Failed to complete payment/account setup."
            );

        }

    };


    /* =====================================================
       CLOSE PAYMENT MODAL
       ===================================================== */

    const closePaymentModal = () => {

        setShowPayment(false);
        setSelectedPlan(null);

    };


    /* =====================================================
       BUTTON TEXT
       ===================================================== */

    const getButtonText = (plan) => {

        /*
         * New signup
         */

        if (!token) {

            if (plan === "FREE") {
                return "Start Free Trial";
            }

            return `Choose ${plan}`;
        }


        /*
         * Existing user
         */

        if (
            isActive &&
            currentPlan === plan
        ) {

            return "Your Current Plan";
        }


        if (
            isPaidActive &&
            plan === "FREE"
        ) {

            return "You can't access this plan";
        }


        if (
            isExpired &&
            plan === "FREE"
        ) {

            return "Start Free Trial";
        }


        return `Choose ${plan}`;

    };


    /* =====================================================
       DISABLED
       ===================================================== */

    const isDisabled = (plan) => {

        if (!token) {
            return false;
        }


        if (
            isActive &&
            currentPlan === plan
        ) {

            return true;
        }


        if (
            isActive &&
            currentPlan !== "FREE" &&
            plan === "FREE"
        ) {

            return true;
        }


        return false;

    };


    /* =====================================================
       UI
       ===================================================== */

    return (
        <>
            {!hideLayout && <Header />}

            <div className="subscription-container">

                <h1 className="subscription-title">
                    Choose Your Justivon Plan
                </h1>

                <p className="subscription-subtitle">
                    Boost visibility. Increase client bookings. Dominate AI rankings.
                </p>


                <PayPalScriptProvider
                    options={{
                        "client-id":
                            paypalClientId,

                        currency:
                            "GBP"
                    }}
                >

                    <div className="plans-wrapper">


                        {/* ================= FREE ================= */}

                        <div className="plan-card">

                            <h3>
                                Free Trial
                            </h3>

                            <div className="price">
                                <FaPoundSign />
                                0
                            </div>

                            <p className="duration">
                                7 Days Access
                            </p>

                            <ul className="features-list">

                                <li>
                                    <FaCheckCircle className="check-icon" />
                                    Profile Listing
                                </li>

                                <li>
                                    <FaCheckCircle className="check-icon" />
                                    Standard AI Visibility
                                </li>

                                <li>
                                    <FaCheckCircle className="check-icon" />
                                    3 Booking Requests
                                </li>

                                <li>
                                    <FaCheckCircle className="check-icon" />
                                    Basic Analytics
                                </li>

                                <li className="restriction">
                                    <FaTimesCircle className="times-icon" />
                                    No AI Boost
                                </li>

                                <li className="restriction">
                                    <FaTimesCircle />
                                    No Export Data
                                </li>

                            </ul>


                            <button
                                className={`plan-btn secondary ${
                                    isDisabled("FREE")
                                        ? "disabled-btn"
                                        : ""
                                }`}
                                onClick={() =>
                                    handlePlanClick("FREE")
                                }
                                disabled={
                                    isDisabled("FREE") ||
                                    processingPlan === "FREE"
                                }
                            >

                                {
                                    processingPlan === "FREE"
                                        ? "Processing..."
                                        : getButtonText("FREE")
                                }

                            </button>

                        </div>


                        {/* ================= PROFESSIONAL ================= */}

                        <div className="plan-card professional">

                            <div className="badge popular">
                                <FaStar />
                                MOST POPULAR
                            </div>

                            <h3>
                                Professional
                            </h3>

                            <div className="price">
                                <FaPoundSign />
                                8.17
                            </div>

                            <p className="duration">
                                Per Month
                            </p>

                            <ul className="features-list">

                                <li>
                                    <FaCheckCircle className="check-icon" />
                                    Priority Search Placement
                                </li>

                                <li>
                                    <FaCheckCircle className="check-icon" />
                                    AI Boost (1.2x)
                                </li>

                                <li>
                                    <FaCheckCircle className="check-icon" />
                                    <FaAward />
                                    Recommended Badge
                                </li>

                                <li>
                                    <FaCheckCircle className="check-icon" />
                                    Booking Analytics
                                </li>

                                <li>
                                    <FaCheckCircle className="check-icon" />
                                    Export Data
                                </li>

                                <li>
                                    <FaCheckCircle className="check-icon" />
                                    30 Bookings / Month
                                </li>

                            </ul>


                            <button
                                className={`plan-btn primary ${
                                    isDisabled("PROFESSIONAL")
                                        ? "disabled-btn"
                                        : ""
                                }`}
                                onClick={() =>
                                    handlePlanClick(
                                        "PROFESSIONAL"
                                    )
                                }
                                disabled={
                                    isDisabled(
                                        "PROFESSIONAL"
                                    )
                                }
                            >

                                {getButtonText(
                                    "PROFESSIONAL"
                                )}

                            </button>

                        </div>


                        {/* ================= ENTERPRISE ================= */}

                        <div className="plan-card enterprise featured">

                            <div className="badge recommend">
                                <FaGem />
                                RECOMMENDED
                            </div>

                            <h3>
                                Enterprise Annual
                            </h3>

                            <div className="price">
                                <FaPoundSign />
                                89.83
                            </div>

                            <p className="duration highlight">
                                <FaCalendarAlt />
                                Pay for 11 months, get 1 FREE
                            </p>

                            <div className="savings-badge">
                                <FaRocket />
                                Save <FaPoundSign />
                                8.17 annually
                            </div>

                            <ul className="features-list">

                                <li>
                                    <FaCheckCircle className="check-icon" />
                                    Top Search Placement
                                </li>

                                <li>
                                    <FaCheckCircle className="check-icon" />
                                    Max AI Boost (1.5x)
                                </li>

                                <li>
                                    <FaCheckCircle className="check-icon" />
                                    <FaCrown />
                                    Gold Badge
                                </li>

                                <li>
                                    <FaCheckCircle className="check-icon" />
                                    Unlimited Bookings
                                </li>

                                <li>
                                    <FaCheckCircle className="check-icon" />
                                    Advanced Analytics
                                </li>

                                <li>
                                    <FaCheckCircle className="check-icon" />
                                    Priority Support
                                </li>

                                <li>
                                    <FaCheckCircle className="check-icon" />
                                    Export Data
                                </li>

                            </ul>


                            <button
                                className={`plan-btn premium ${
                                    isDisabled("ENTERPRISE")
                                        ? "disabled-btn"
                                        : ""
                                }`}
                                onClick={() =>
                                    handlePlanClick(
                                        "ENTERPRISE"
                                    )
                                }
                                disabled={
                                    isDisabled(
                                        "ENTERPRISE"
                                    )
                                }
                            >

                                {getButtonText(
                                    "ENTERPRISE"
                                )}

                            </button>

                        </div>

                    </div>


                    {/* ================= PAYMENT MODAL ================= */}

                    {showPayment &&
                        selectedPlan && (

                        <div
                            className="payment-modal-overlay"
                            onClick={
                                closePaymentModal
                            }
                        >

                            <div
                                className="payment-modal"
                                onClick={(e) =>
                                    e.stopPropagation()
                                }
                            >

                                <button
                                    className="modal-close-btn"
                                    onClick={
                                        closePaymentModal
                                    }
                                >
                                    ×
                                </button>


                                <h2>
                                    Complete Your Payment
                                </h2>

                                <p className="modal-plan-name">
                                    Selected Plan:{" "}
                                    {selectedPlan}
                                </p>


                                <div className="payment-options">


                                    {/* ================= PROFESSIONAL ================= */}

                                    {selectedPlan ===
                                        "PROFESSIONAL" && (

                                        <div className="payment-option">

                                            <h3>
                                                Monthly Subscription
                                            </h3>

                                            <p className="payment-amount">
                                                <FaPoundSign />
                                                8.17 / month
                                            </p>


                                            <div className="paypal-button-container">

                                                <PayPalButtons

                                                    style={{
                                                        layout:
                                                            "vertical"
                                                    }}

                                                    createOrder={(
                                                        data,
                                                        actions
                                                    ) =>
                                                        actions.order.create(
                                                            {
                                                                purchase_units:
                                                                    [
                                                                        {
                                                                            description:
                                                                                "Professional Plan - Monthly",

                                                                            amount:
                                                                                {
                                                                                    value:
                                                                                        "8.17",

                                                                                    currency_code:
                                                                                        "GBP"
                                                                                }
                                                                        }
                                                                    ]
                                                            }
                                                        )
                                                    }


                                                    onApprove={
                                                        async (
                                                            data,
                                                            actions
                                                        ) => {

                                                            try {

                                                                const details =
                                                                    await actions.order.capture();

                                                                await handlePaymentSuccess(
                                                                    details,
                                                                    "PROFESSIONAL",
                                                                    "MONTHLY",
                                                                    8.17
                                                                );

                                                            } catch (
                                                                error
                                                            ) {

                                                                console.error(
                                                                    "PayPal capture error:",
                                                                    error
                                                                );

                                                                toast.error(
                                                                    "Payment capture failed"
                                                                );

                                                            }

                                                        }
                                                    }


                                                    onCancel={() =>
                                                        toast.error(
                                                            "Payment Cancelled"
                                                        )
                                                    }


                                                    onError={() =>
                                                        toast.error(
                                                            "Payment Failed"
                                                        )
                                                    }

                                                />

                                            </div>

                                        </div>

                                    )}


                                    {/* ================= ENTERPRISE ================= */}

                                    {selectedPlan ===
                                        "ENTERPRISE" && (

                                        <div className="payment-option">

                                            <h3>
                                                Annual Subscription
                                            </h3>

                                            <p className="payment-amount">

                                                <FaPoundSign />
                                                89.83 / year{" "}

                                                <span className="savings-tag">
                                                    Save 1 month
                                                </span>

                                            </p>


                                            <div className="paypal-button-container">

                                                <PayPalButtons

                                                    style={{
                                                        layout:
                                                            "vertical"
                                                    }}

                                                    createOrder={(
                                                        data,
                                                        actions
                                                    ) =>
                                                        actions.order.create(
                                                            {
                                                                purchase_units:
                                                                    [
                                                                        {
                                                                            description:
                                                                                "Enterprise Plan - Annual",

                                                                            amount:
                                                                                {
                                                                                    value:
                                                                                        "89.83",

                                                                                    currency_code:
                                                                                        "GBP"
                                                                                }
                                                                        }
                                                                    ]
                                                            }
                                                        )
                                                    }


                                                    onApprove={
                                                        async (
                                                            data,
                                                            actions
                                                        ) => {

                                                            try {

                                                                const details =
                                                                    await actions.order.capture();

                                                                await handlePaymentSuccess(
                                                                    details,
                                                                    "ENTERPRISE",
                                                                    "ANNUAL",
                                                                    89.83
                                                                );

                                                            } catch (
                                                                error
                                                            ) {

                                                                console.error(
                                                                    "PayPal capture error:",
                                                                    error
                                                                );

                                                                toast.error(
                                                                    "Payment capture failed"
                                                                );

                                                            }

                                                        }
                                                    }


                                                    onCancel={() =>
                                                        toast.error(
                                                            "Payment Cancelled"
                                                        )
                                                    }


                                                    onError={() =>
                                                        toast.error(
                                                            "Payment Failed"
                                                        )
                                                    }

                                                />

                                            </div>

                                        </div>

                                    )}

                                </div>


                                <p className="payment-security-note">
                                    <FaShieldAlt />
                                    Your payment is securely processed by PayPal
                                </p>

                            </div>

                        </div>

                    )}

                </PayPalScriptProvider>


                {/* ================= PLAN COMPARISON ================= */}

                <div className="comparison-section">

                    <h2>
                        Plan Comparison
                    </h2>

                    <div className="table-responsive">

                        <table className="comparison-table">

                            <thead>

                                <tr>

                                    <th>
                                        Features
                                    </th>

                                    <th>
                                        Free
                                    </th>

                                    <th>
                                        Professional
                                    </th>

                                    <th>
                                        Enterprise Annual
                                    </th>

                                </tr>

                            </thead>

                            <tbody>

                                <tr>

                                    <td className="feature-name">
                                        Profile Listings
                                    </td>

                                    <td className="feature-value">
                                        1
                                    </td>

                                    <td className="feature-value">
                                        30 / month
                                    </td>

                                    <td className="feature-value">
                                        Unlimited
                                    </td>

                                </tr>


                                <tr>

                                    <td className="feature-name">
                                        AI Ranking Boost
                                    </td>

                                    <td className="feature-value">
                                        <FaTimesCircle />
                                    </td>

                                    <td className="feature-value">
                                        1.2x
                                    </td>

                                    <td className="feature-value">
                                        1.5x
                                    </td>

                                </tr>


                                <tr>

                                    <td className="feature-name">
                                        Badge
                                    </td>

                                    <td className="feature-value">
                                        <FaTimesCircle />
                                    </td>

                                    <td className="feature-value">
                                        <FaAward />
                                        Recommended
                                    </td>

                                    <td className="feature-value">
                                        <FaCrown />
                                        Gold
                                    </td>

                                </tr>


                                <tr>

                                    <td className="feature-name">
                                        Chat Option
                                    </td>

                                    <td className="feature-value">
                                        <FaTimesCircle />
                                    </td>

                                    <td className="feature-value">
                                        <FaCheckCircle />
                                    </td>

                                    <td className="feature-value">
                                        <FaCheckCircle />
                                    </td>

                                </tr>


                                <tr>

                                    <td className="feature-name">
                                        Analytics
                                    </td>

                                    <td className="feature-value">
                                        Basic
                                    </td>

                                    <td className="feature-value">
                                        Advanced
                                    </td>

                                    <td className="feature-value">
                                        Advanced + Insights
                                    </td>

                                </tr>


                                <tr>

                                    <td className="feature-name">
                                        Priority Support
                                    </td>

                                    <td className="feature-value">
                                        <FaTimesCircle />
                                    </td>

                                    <td className="feature-value">
                                        <FaTimesCircle />
                                    </td>

                                    <td className="feature-value">
                                        <FaCheckCircle />
                                    </td>

                                </tr>


                                <tr>

                                    <td className="feature-name">
                                        Dedicated Account Manager
                                    </td>

                                    <td className="feature-value">
                                        <FaTimesCircle />
                                    </td>

                                    <td className="feature-value">
                                        <FaTimesCircle />
                                    </td>

                                    <td className="feature-value">
                                        <FaCheckCircle />
                                    </td>

                                </tr>


                                <tr>

                                    <td className="feature-name">
                                        Annual Discount
                                    </td>

                                    <td className="feature-value">
                                        <FaTimesCircle />
                                    </td>

                                    <td className="feature-value">
                                        <FaTimesCircle />
                                    </td>

                                    <td className="feature-value highlight-cell">
                                        1 Month Free
                                    </td>

                                </tr>

                            </tbody>

                        </table>

                    </div>

                </div>


                {/* ================= PAYMENT SECURITY ================= */}

                <div className="payment-security">

                    <FaShieldAlt />

                    All payments are securely processed with industry-standard encryption.

                </div>


                {/* ================= TESTIMONIALS ================= */}

                <div className="testimonial-section">

                    <h2>
                        What Solicitors Say About Justivon
                    </h2>


                    <div className="testimonial-wrapper">

                        <div className="testimonial-card">

                            <p>
                                "The Professional plan increased my case inquiries within the first month."
                            </p>

                            <h4>
                                James Carter
                            </h4>

                            <span>
                                Criminal Defense Solicitor
                            </span>

                        </div>


                        <div className="testimonial-card">

                            <p>
                                "Enterprise gave our firm premium homepage visibility and consistent high-value clients."
                            </p>

                            <h4>
                                Olivia Bennett
                            </h4>

                            <span>
                                Family Law Specialist
                            </span>

                        </div>


                        <div className="testimonial-card">

                            <p>
                                "Started with Free trial and upgraded quickly. Analytics helped track conversions."
                            </p>

                            <h4>
                                Daniel Wright
                            </h4>

                            <span>
                                Immigration Law Specialist
                            </span>

                        </div>

                    </div>

                </div>

            </div>

            {!hideLayout && <Footer />}

        </>
    );
}